import { useDispatch, useSelector } from "react-redux";
import { IEditDocumentPopup } from "./edit-document-popup";
import {
  addCommonProperties,
  initialDocumentDataToSave,
  SET_DOCUMENT_DATA_TO_SAVE,
  UPDATE_EPIC_NOTES_SELECTED_ROWS,
} from "./reducers/document-management-reducer";
import React, { useCallback, useEffect, useMemo } from "react";
import { actions as asyncActions } from "async-ops";
import { bindActionCreators } from "redux";
import { groupBy } from "lodash";
import DataGrid, {
  Column,
  FilterRow,
  HeaderFilter,
  MasterDetail,
  Pager,
  Paging,
  Selection,
} from "devextreme-react/data-grid";
import { callOperation, actions } from "async-ops";
import { GET_EHR_DOCUMENTS } from "./services/get-ehr-documents";
import { HIDE_LOADER, SHOW_LOADER } from "../mdae-common-reducer";
import {
  EDIT_OR_ATTACH_COMPONENT_MODES,
  EditOrAttachDocument,
} from "./edit-or-attach-document";
import { convertDateFormat } from "../../utils/dateUtils";
import { IMPORT_EHR_DOCUMENTATION } from "./services/import-ehr-documents";
import getDocumentPreviewColumn from "./edit-or-attach-document";
import MdaeDataGrid from "../common_widgets/data-grid";

// Master-detail child grid: shows the documents for a single patient row.
// Receives the DevExtreme master-detail template arg, where `data.data` is the
// grouped patient row built in EhrDocumentsComponent's `patientGroups`.
const EhrPatientDocumentsDetail = ({ data }) => {
  const dispatch = useDispatch();
  const asyncAction = bindActionCreators(
    { asyncOperationStart: asyncActions.asyncOperationStart },
    dispatch,
  );
  const attachedLevelName = useSelector(
    (state) => state["documentManagementReducer"].attachedLevelName,
  );
  const attachedLevelId = useSelector(
    (state) => state["documentManagementReducer"].attachedLevelId,
  );
  const selectedEhrDocuments: any[] = useSelector(
    (state: any) =>
      state["documentManagementReducer"].ehrConnectorData.selectedEhrDocuments,
  );

  const documents = data?.data?.documents ?? [];
  // Restore this patient's current selection on (re)mount — detail grids
  // remount whenever a row is collapsed/expanded.
  const initiallySelectedKeys = documents
    .map((doc) => doc.binaryId)
    .filter((id) => selectedEhrDocuments.includes(id));

  const onDetailSelectionChanged = useCallback(
    (e) => {
      dispatch({
        type: UPDATE_EPIC_NOTES_SELECTED_ROWS,
        data: {
          added: e.currentSelectedRowKeys,
          removed: e.currentDeselectedRowKeys,
        },
      });
    },
    [dispatch],
  );

  return (
    <MdaeDataGrid
      id={data.data.patientKey + "_grdEhrPatients"}
      dataSource={documents}
      keyExpr={"binaryId"}
      defaultSelectedRowKeys={initiallySelectedKeys}
      onSelectionChanged={onDetailSelectionChanged}
      showBorders={true}
      clearFilterButtonId="btn-ehr-list-grid-clear-filter"
      export={{ enabled: true, fileName: "Document List" }}
    >
      {getDocumentPreviewColumn(
        asyncAction,
        attachedLevelId,
        attachedLevelName,
        dispatch,
      )}
      <Column dataField="type" caption="Type" />
      <Column dataField="category" caption="Category" />
      <Column dataField="participants" caption="Participants" />
      <Column dataField="dateStart" caption="Service From" />
      <Column dataField="dateEnd" caption="Service To" />
      <Selection mode={"multiple"} />
      <Paging defaultPageSize={10} />
      <Pager
        showPageSizeSelector={true}
        showNavigationButtons={true}
        allowedPageSizes={[10, 20, 30]}
        showInfo={true}
        visible={true}
      />
      <FilterRow visible={true} />
      <HeaderFilter visible={true} />
    </MdaeDataGrid>
  );
};

interface IEhrDocumentationComponent extends IEditDocumentPopup {}
const EhrDocumentsComponent: React.FC<IEhrDocumentationComponent> = (props) => {
  const dispatch = useDispatch();
  const asyncAction = bindActionCreators(
    {
      asyncOperationStart: asyncActions.asyncOperationStart,
    },
    dispatch,
  );
  const dataSourceStoreId = props.uniqueId + EDIT_OR_ATTACH_COMPONENT_MODES.EHR;
  const documentDataToSave: Array<typeof initialDocumentDataToSave> = useSelector(
    (state: any) =>
      state["documentManagementReducer"].documentDataToSaveStore[
        dataSourceStoreId
      ],
  );
  const ehrDocuments = useSelector(
    (state: any) =>
      state["documentManagementReducer"].ehrConnectorData.ehrDocuments,
  );
  const completeCaseDetail = useSelector(
    (state) => state["caseDetailReducer"].completeCaseDetail
  );

  // Group the flat EHR document list into one row per patient (last name,
  // first name, DOB). This grouped array is the parent grid's data source;
  // each patient's documents are shown in the master-detail child grid.
  const patientGroups = useMemo(() => {
    const grouped = groupBy(
      ehrDocuments ?? [],
      (d) => `${d.lastName}|${d.firstName}|${d.dateOfBirth}`,
    );
    return Object.entries(grouped).map(([patientKey, documents]) => ({
      patientKey,
      lastName: documents[0].lastName,
      firstName: documents[0].firstName,
      dateOfBirth: documents[0].dateOfBirth,
      dos: documents[0].dos,
      mrn: documents[0].mrn,
      documents,
    }));
  }, [ehrDocuments]);

  useEffect(() => {
    const dateOfBirth = convertDateFormat(completeCaseDetail?.dob, "YYYY-MM-DD") || '';
    const mrn = completeCaseDetail?.patientMRN;
    const lastName = completeCaseDetail?.patientLastName;
    const firstName = completeCaseDetail?.patientFirstName;
    const dateStart = completeCaseDetail?.statementFromDate
      ? convertDateFormat(completeCaseDetail.statementFromDate, "YYYY-MM-DD")
      : undefined;
    const dateEnd = completeCaseDetail?.statementToDate
      ? convertDateFormat(completeCaseDetail.statementToDate, "YYYY-MM-DD")
      : undefined;
    asyncAction.asyncOperationStart(GET_EHR_DOCUMENTS, {
      dateOfBirth,
      mrn,
      lastName,
      firstName,
      dateStart,
      dateEnd,
    });
  }, []);

  return (
    <>
      <div className={documentDataToSave?.length == 0 ? "hide-control" : ""}>
        <EditOrAttachDocument
          {...props}
          mode={EDIT_OR_ATTACH_COMPONENT_MODES.EHR}
        />
      </div>
      <div
        className={documentDataToSave?.length > 0 ? "hide-control" : ""}
      >
        <DataGrid
          id={props.uniqueId + "_grdEhrPatients"}
          dataSource={patientGroups}
          keyExpr={"patientKey"}
        >
          <HeaderFilter visible={true} />
          <Column dataField="lastName" caption="Last Name" />
          <Column dataField="firstName" caption="First Name" />
          <Column dataField="dateOfBirth" caption="DOB" />
          <Column dataField="mrn" caption="MRN" />
          <Column dataField="dos" caption="DOS" />
          <MasterDetail
            enabled={true}
            autoExpandAll={true}
            component={EhrPatientDocumentsDetail}
          />
          <Pager visible={false} />
          <HeaderFilter visible={true} />
        </DataGrid>
      </div>
    </>
  );
};
export const importEhrDocumentation = (uniqueId) => {
  return (dispatch, getState) => {
    const state = getState();
    const ehrDocuments = state["documentManagementReducer"].ehrConnectorData.ehrDocuments;
    const selectedEhrDocuments: any[] =
      state["documentManagementReducer"].ehrConnectorData.selectedEhrDocuments;

    const documentsToImport = selectedEhrDocuments.map((selectedNoteId) => {
      const selectedNote = ehrDocuments.find(
        (note) => note.binaryId === selectedNoteId,
      );
      return {
        binaryId: selectedNote.binaryId,
        contentType: selectedNote.contentType,
        documentStorageBaseKey: "doc-repo/ehr/"
      };
    });

    dispatch({ type: SHOW_LOADER });
    callOperation(IMPORT_EHR_DOCUMENTATION, {
      ehrDocumentReadInformation: documentsToImport,
    }).then((response) => {
      dispatch({ type: HIDE_LOADER });
      response.forEach((x) => {
        const selectedNote = ehrDocuments.find(
          (note) => note.binaryId === x.binaryId,
        );
        addCommonProperties(x);
        x.documentName = `${selectedNote.type} - ${selectedNote.category}`;
        x.s3ObjectName = x.key;
        x.originalFileName = (x.key as string).split("/").slice(-1)[0];
        x.uploadProgress = null;
      });

      dispatch({
        type: SET_DOCUMENT_DATA_TO_SAVE,
        data: {
          documentDataToSave: response,
          uniqueId: uniqueId,
        },
      });
    });
  };
};


export default EhrDocumentsComponent;
